import { useEffect, useMemo, useState } from "react";

import T, { GetLang } from "./translations/Translations";

import { useGlobalContext } from "./contextProvider/ContextProvider";
import { useOpenEnrollmentWindows } from "@hooks/useOpenEnrollmentWindows";
import WelcomeAccordions from "./WelcomeAccordions";

import "@/styles/WelcomeText.scss";

/**
 * For external guardians, determine what instructions to display based on the appropriate OE windows
 * @name WelcomeText
 * @returns {JSX.Element}
 */
const WelcomeText = (): JSX.Element => {
    const { state } = useGlobalContext();
    const { oeActionControls, oeDistrictWindows, studentsOe } = state || {};
    const lang = GetLang();

    const { intentToReturn, parentOffer, parentRequest, parentRequestRound2, windowsReady } = useOpenEnrollmentWindows(
        oeActionControls,
        oeDistrictWindows
    );

    // The dependency array is empty, ensuring it's only calculated once
    const initialActiveKeys = useMemo(() => {
        return ["0", "1", "2", "3", "4"];
    }, []);
    // Changed initial state to an array of event keys to keep them open by default
    const [activeKeys, setActiveKeys] = useState(initialActiveKeys);

    ///////////////////////////////////////////////////////////////////////////
    // Translations
    // declared here to prevent rule of hooks violations
    //-------------------------------------------------------------------------
    const welcomeTagline = T({ tKey: "Welcome_Tagline", langStr: lang });
    const welcomeP1 = T({ tKey: "Welcome_p1", langStr: lang });
    const welcomeP2 = T({ tKey: "Welcome_p2", langStr: lang });
    const welcomeH51 = T({ tKey: "Welcome_h5_1", langStr: lang });

    // This is the core fix. Update the state to an empty array (closed)
    // if students are present, or back to the initial keys (open) if not.
    useEffect(() => {
        if ((studentsOe?.length ?? 0) > 0) {
            // Close all accordions by setting activeKeys to an empty array
            setActiveKeys([]);
        } else {
            // Keep all accordions open by resetting to the initial keys
            setActiveKeys(initialActiveKeys);
        }
    }, [initialActiveKeys, studentsOe]);

    // Handler to allow the user to open/close accordions after the initial load
    // The 'eventKey' parameter is a string or an array of strings in 'alwaysOpen' mode
    const handleAccordionSelect = (key: string | string[] | null | undefined): void => {
        if (Array.isArray(key)) {
            setActiveKeys(key);
        } else {
            setActiveKeys(key ? [key] : []);
        }
    };

    return (
        <div className="welcome-text-container">
            <h4>{welcomeTagline}</h4>
            {welcomeP1}
            {welcomeP2}
            {welcomeH51}
            <br />
            <WelcomeAccordions
                activeKeys={activeKeys}
                handleAccordionSelect={handleAccordionSelect}
                intentToReturn={intentToReturn}
                oeDistrictWindows={oeDistrictWindows}
                oeWindowsFlag={windowsReady}
                parentOffer={parentOffer}
                parentRequest={parentRequest}
                parentRequestRound2={parentRequestRound2}
            />
        </div>
    );
};

export default WelcomeText;
